import type { DensitySnapshot } from "@screener/protocol";
import { type DensitySort, defaultSortDirection, sortRows } from "./derive";

/**
 * Вид «по монете» §9 (1.21) — группировка уже отобранных строк по базовой
 * монете §3.2 поверх бирж, рынков и котировок. Представитель и порядок групп
 * — то же правило, что у карточек (`buildCards` в `cards.ts`, design.md
 * change stage6-coin-view, решение 1): `sortRows` по ключу экрана под
 * `defaultSortDirection` выбирает представителя, затем `sortRows`
 * представителей под сортировкой экрана задаёт порядок групп. В отличие от
 * карточек, строки внутри группы всегда идут по дистанции — сортировка
 * экрана строки группы не переставляет (§9 1.21).
 */

export interface CoinGroup {
  base: string;
  rows: DensitySnapshot[];
  /** Наименьшая известная дистанция группы, `null` — ни одна не известна. */
  nearestDistancePct: number | null;
}

/**
 * Группирует строки по базовой монете, выбирает представителя каждой группы
 * и упорядочивает группы по представителям под сортировкой экрана — тем же
 * приёмом, что `buildCards`. Строки внутри группы идут по дистанции по
 * возрастанию (`sortRows` с ключом `distance`): неизвестная дистанция уже
 * уходит в конец её же компаратором. `nearestDistancePct` берётся из первой
 * строки после этой перестановки.
 */
export function buildCoinGroups(rows: DensitySnapshot[], sort: DensitySort): CoinGroup[] {
  const byBase = new Map<string, CoinGroup>();
  for (const row of rows) {
    const base = row.instrument.base;
    const group = byBase.get(base);
    if (group === undefined) {
      byBase.set(base, { base, rows: [row], nearestDistancePct: null });
    } else {
      group.rows.push(row);
    }
  }

  // Представитель группы — первая строка под своим, не экранным, направлением
  // (`defaultSortDirection`): тот же приём, что у `buildCards` — выбор
  // представителя не зависит от того, в какую сторону сейчас отсортирован
  // экран.
  const representativeDirection = defaultSortDirection(sort.key);
  const groupByRepresentativeId = new Map<string, CoinGroup>();
  const representatives: DensitySnapshot[] = [];
  for (const group of byBase.values()) {
    const [best] = sortRows(group.rows, { key: sort.key, direction: representativeDirection });
    if (best === undefined) {
      // Группа заводится только при появлении строки — пустой не бывает.
      continue;
    }
    representatives.push(best);
    groupByRepresentativeId.set(best.density_id, group);
  }

  // Порядок групп — порядок их представителей под сортировкой экрана.
  const orderedGroups = sortRows(representatives, sort)
    .map((representative) => groupByRepresentativeId.get(representative.density_id))
    .filter((group): group is CoinGroup => group !== undefined);

  for (const group of orderedGroups) {
    group.rows = sortRows(group.rows, { key: "distance", direction: "asc" });
    group.nearestDistancePct = group.rows[0]?.distance_pct ?? null;
  }

  return orderedGroups;
}
